import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../lib/api';
import { fetchAttachmentBlobUrl } from '../../lib/attachmentCache';
import { Button } from '../ui/button';
import {
  Paperclip, Upload, X, File as FileIcon, Image as ImageIcon, Download, Loader2, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB — must match backend.

const formatBytes = (b) => {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

const isImage = (mime) => typeof mime === 'string' && mime.startsWith('image/');

/**
 * Reusable attachments field for the task create / edit / detail surfaces.
 *
 * Two modes:
 *   • Live mode (taskId truthy) — uploads immediately and lists / deletes
 *     from the server. Used by Edit and Detail views.
 *   • Draft mode (taskId falsy) — buffers selected `File` objects in
 *     local state and bubbles them up via `onPendingChange` so the
 *     parent (Create dialog) can upload them after the task is created.
 *
 * Supports: file picker, drag-and-drop, paste-from-clipboard. The
 * paste handler attaches to `window` so an image copied with the
 * standard OS screenshot tool (Cmd-Shift-Ctrl-4 / PrtScr) lands in
 * the dialog regardless of which input has focus.
 */
export default function TaskAttachmentsField({
  taskId = null,
  pendingFiles = [],
  onPendingChange,
  canEdit = true,
  canDelete = true,
  data_testid = 'task-attachments',
}) {
  const isLive = !!taskId;
  const [serverAttachments, setServerAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingNames, setUploadingNames] = useState([]); // for live mode visual indication
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const fileInputRef = useRef(null);
  const rootRef = useRef(null);

  // ------- Server fetch (live mode) -------
  const refreshFromServer = useCallback(async () => {
    if (!isLive) return;
    setLoading(true);
    try {
      const res = await api.get(`/tasks/${taskId}/attachments`);
      if (res.data?.success) setServerAttachments(res.data.data || []);
    } catch (err) {
      // Surface only real errors — 404 (task gone) is silently ignored
      // because the dialog parent already handles missing-task UX.
      if (err?.response?.status && err.response.status !== 404) {
        toast.error('Could not load attachments');
      }
    } finally {
      setLoading(false);
    }
  }, [isLive, taskId]);

  useEffect(() => { if (isLive) refreshFromServer(); }, [isLive, refreshFromServer]);

  // ------- Validation -------
  const acceptFiles = useCallback((rawFiles) => {
    const accepted = [];
    for (const f of rawFiles) {
      if (!f) continue;
      if (f.size > MAX_FILE_BYTES) {
        toast.error(`"${f.name}" is ${formatBytes(f.size)} — max is 10 MB.`);
        continue;
      }
      accepted.push(f);
    }
    return accepted;
  }, []);

  // ------- Live upload -------
  const uploadOne = useCallback(async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.post(`/tasks/${taskId}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data?.data;
  }, [taskId]);

  const uploadFiles = useCallback(async (files) => {
    const ok = acceptFiles(files);
    if (ok.length === 0) return;

    if (!isLive) {
      // Draft mode — just buffer & let the parent upload after create.
      onPendingChange?.([...(pendingFiles || []), ...ok]);
      return;
    }

    setUploadingNames(prev => [...prev, ...ok.map(f => f.name)]);
    try {
      // Upload sequentially so the server stream stays sane for big files
      // and so any failure halts the rest with a clear message.
      const uploaded = [];
      for (const f of ok) {
        try {
          const att = await uploadOne(f);
          if (att) uploaded.push(att);
        } catch (err) {
          toast.error(err?.response?.data?.detail || `Failed to upload "${f.name}"`);
        }
      }
      if (uploaded.length) {
        toast.success(`${uploaded.length} attachment${uploaded.length > 1 ? 's' : ''} added`);
        setServerAttachments(prev => [...prev, ...uploaded]);
      }
    } finally {
      setUploadingNames(prev => prev.filter(n => !ok.some(f => f.name === n)));
    }
  }, [acceptFiles, isLive, onPendingChange, pendingFiles, uploadOne]);

  // ------- Paste handler (global so screenshot paste works regardless of focus) -------
  useEffect(() => {
    const handler = (e) => {
      // Only react to paste events when our dialog/page is mounted.
      // The root ref's nearest dialog ancestor is enough — if the user
      // pastes outside a task surface, this component isn't there.
      const items = e.clipboardData?.items;
      if (!items || items.length === 0) return;
      const files = [];
      for (const it of items) {
        if (it.kind === 'file') {
          const f = it.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length) {
        e.preventDefault();
        uploadFiles(files);
      }
    };
    window.addEventListener('paste', handler);
    return () => window.removeEventListener('paste', handler);
  }, [uploadFiles]);

  // ------- Drag/drop handlers -------
  const onDragOver = (e) => { e.preventDefault(); setIsDraggingOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); setIsDraggingOver(false); };
  const onDrop = (e) => {
    e.preventDefault();
    setIsDraggingOver(false);
    if (e.dataTransfer.files?.length) uploadFiles(Array.from(e.dataTransfer.files));
  };

  const onPickerChange = (e) => {
    if (e.target.files?.length) uploadFiles(Array.from(e.target.files));
    // Reset so picking the same file twice still fires onChange
    e.target.value = '';
  };

  // ------- Delete handlers -------
  const removePending = (idx) => {
    onPendingChange?.((pendingFiles || []).filter((_, i) => i !== idx));
  };

  const deleteLive = async (att) => {
    try {
      await api.delete(`/tasks/attachments/${att.id}`);
      setServerAttachments(prev => prev.filter(a => a.id !== att.id));
      toast.success('Attachment deleted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  // ------- Download URL builder -------
  // We can't rely on a plain <a href> because the endpoint requires the
  // Authorization header. Instead, fetch as blob and trigger a download.
  const downloadLive = async (att) => {
    try {
      const res = await api.get(`/tasks/attachments/${att.id}/download`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.original_filename || att.stored_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  // ------- Thumbnail URLs for live image attachments (lazy) -------
  // We render an <img> with a blob URL fetched on demand to avoid loading
  // every attachment up front. For now we eagerly preload images since
  // task attachments lists will typically be small (< 20).
  const [thumbs, setThumbs] = useState({}); // att.id -> objectURL
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const a of serverAttachments) {
        if (!isImage(a.content_type)) continue;
        try {
          const url = await fetchAttachmentBlobUrl(a.id);
          if (cancelled) return;
          setThumbs(prev => prev[a.id] ? prev : { ...prev, [a.id]: url });
        } catch {
          /* ignore — fall back to icon */
        }
      }
    })();
    return () => { cancelled = true; };
  }, [serverAttachments]);

  // ------- Pending file thumbnails (object URLs) -------
  const pendingThumbs = useMemo(() => {
    return (pendingFiles || []).map(f => (
      f.type?.startsWith('image/') ? URL.createObjectURL(f) : null
    ));
  }, [pendingFiles]);
  useEffect(() => () => pendingThumbs.forEach(u => u && URL.revokeObjectURL(u)),
    [pendingThumbs]);

  return (
    <div ref={rootRef} className="space-y-2" data-testid={data_testid}>
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium leading-none flex items-center gap-1.5">
          <Paperclip className="h-3.5 w-3.5" /> Attachments
        </label>
        <span className="text-[11px] text-muted-foreground">
          Up to 10 MB · drag, paste or pick files
        </span>
      </div>

      {canEdit && (
        <div
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`flex items-center justify-center gap-2 rounded-md border border-dashed px-3 py-4 cursor-pointer transition-colors text-xs text-muted-foreground ${
            isDraggingOver ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:bg-muted/30'
          }`}
          data-testid={`${data_testid}-dropzone`}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
        >
          <Upload className="h-4 w-4" />
          <span>
            <span className="font-medium text-foreground">Click to upload</span>
            {' '}or drag-drop · Cmd/Ctrl-V also pastes screenshots
          </span>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={onPickerChange}
            className="hidden"
            data-testid={`${data_testid}-input`}
          />
        </div>
      )}

      {/* Pending (draft) files — not yet on server */}
      {pendingFiles?.length > 0 && (
        <ul className="space-y-1.5" data-testid={`${data_testid}-pending-list`}>
          {pendingFiles.map((f, idx) => (
            <li
              key={`${f.name}-${idx}`}
              className="flex items-center gap-2 rounded-md border border-dashed border-amber-500/40 bg-amber-500/5 px-2 py-1.5 text-xs"
            >
              {pendingThumbs[idx] ? (
                <img src={pendingThumbs[idx]} alt={f.name} className="h-8 w-8 rounded object-cover" />
              ) : (
                <FileIcon className="h-4 w-4 text-muted-foreground" />
              )}
              <div className="flex-1 min-w-0">
                <p className="truncate">{f.name}</p>
                <p className="text-[10px] text-muted-foreground">
                  {formatBytes(f.size)} · uploads after Create
                </p>
              </div>
              <Button
                type="button" variant="ghost" size="icon" className="h-6 w-6"
                onClick={() => removePending(idx)}
                data-testid={`${data_testid}-pending-remove-${idx}`}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {/* Live uploading indicators */}
      {uploadingNames.length > 0 && (
        <ul className="space-y-1.5">
          {uploadingNames.map((n, i) => (
            <li key={`${n}-${i}`} className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading {`"${n}"`}…
            </li>
          ))}
        </ul>
      )}

      {/* Server-side list */}
      {loading ? (
        <p className="text-xs text-muted-foreground">Loading attachments…</p>
      ) : serverAttachments.length > 0 ? (
        <ul className="space-y-1.5" data-testid={`${data_testid}-list`}>
          {serverAttachments.map(att => (
            <li
              key={att.id}
              className="flex items-center gap-2 rounded-md border border-border bg-card/50 px-2 py-1.5 text-xs"
              data-testid={`${data_testid}-item-${att.id}`}
            >
              {isImage(att.content_type) && thumbs[att.id] ? (
                <img
                  src={thumbs[att.id]}
                  alt={att.original_filename}
                  className="h-10 w-10 rounded object-cover"
                />
              ) : isImage(att.content_type) ? (
                <ImageIcon className="h-4 w-4 text-muted-foreground" />
              ) : (
                <FileIcon className="h-4 w-4 text-muted-foreground" />
              )}
              <div className="flex-1 min-w-0">
                <p className="truncate font-medium text-foreground">{att.original_filename}</p>
                <p className="text-[10px] text-muted-foreground">
                  {formatBytes(att.size_bytes)} · {att.uploaded_by_name || 'Unknown'}
                </p>
              </div>
              <Button
                type="button" variant="ghost" size="icon" className="h-7 w-7"
                onClick={() => downloadLive(att)}
                data-testid={`${data_testid}-download-${att.id}`}
                title="Download"
              >
                <Download className="h-3.5 w-3.5" />
              </Button>
              {canDelete && (
                <Button
                  type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => deleteLive(att)}
                  data-testid={`${data_testid}-delete-${att.id}`}
                  title="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
